import { FC, useCallback, useRef, useState } from 'react';

import { AvailableCurrencies, CURRENCY_SYMBOLS, getFormattedIntNumber } from 'utils/clickme';

import {
    ActionBar,
    BottomSheet,
    Button,
    Card,
    Cell,
    CellText,
    Layer,
    Modal,
    NavBar,
    NumberInput,
    Text,
    TextList,
    useBreakpoint,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { QuestionCircleOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { fetchRefund } from 'components/common/BalanceCardContent/BalanceCardTop/AdditionalActions/RefundModal/fetchRefund';
import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';
import { TooltipCommon } from 'components/common/TooltipCommon/TooltipCommon';

import { selectBalance } from 'store/models/balance';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/common/BalanceCardContent/BalanceCardTop/AdditionalActions/RefundModal/RefundModal.module.less';

interface RefundModalProps {
    visible: boolean;
    onClose: () => void;
}

const TrlKeys = {
    title: 'Header.HeaderBalanceDrop.AdditionalActions.refund.title',
    description: 'Header.HeaderBalanceDrop.AdditionalActions.refund.description',
    buttons: {
        confirm: 'Header.HeaderBalanceDrop.AdditionalActions.refund.buttons.confirm',
        cancel: 'Header.HeaderBalanceDrop.AdditionalActions.refund.buttons.cancel',
    },
    input: {
        placeholder: 'Header.HeaderBalanceDrop.AdditionalActions.refund.input.placeholder',
        validation: {
            empty: 'Header.HeaderBalanceDrop.AdditionalActions.refund.input.validation.empty',
            notEnoughBalance: 'Header.HeaderBalanceDrop.AdditionalActions.refund.input.validation.notEnoughBalance',
        },
    },
    heldTip: 'Header.HeaderBalanceDrop.AdditionalActions.refund.heldTip',
    tooltip: {
        list1: 'Header.HeaderBalanceDrop.AdditionalActions.refund.tooltip.list.1',
        list2: 'Header.HeaderBalanceDrop.AdditionalActions.refund.tooltip.list.2',
        list3: 'Header.HeaderBalanceDrop.AdditionalActions.refund.tooltip.list.3',
        list4: 'Header.HeaderBalanceDrop.AdditionalActions.refund.tooltip.list.4',
        list5: 'Header.HeaderBalanceDrop.AdditionalActions.refund.tooltip.list.5',
    },
};

export const RefundModal: FC<RefundModalProps> = ({ visible, onClose }) => {
    const { realBalance, heldBalance, refundableBalance, reservedBalance, heldRefundableBalance } =
        useSelector(selectBalance);

    const displayReservedBalance = Math.min(reservedBalance, realBalance);
    const displayHeldBalance = Math.min(heldBalance, Math.max(0, realBalance - displayReservedBalance));

    const [isTooltipVisible, setIsTooltipVisibleOn, setIsTooltipVisibleOff] = useOnOffState(false);
    const [refundAmount, setRefundAmount] = useState('');
    const [validationError, setValidationError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const trls = useTrls();
    const dispatch = useDispatch();
    const { isMobile } = useBreakpoint();
    const activatorRef = useRef<HTMLDivElement>(null);

    const handleInputChange = useCallback(
        (value: string) => {
            if (Number(value) > refundableBalance) {
                setValidationError(trls(TrlKeys.input.validation.notEnoughBalance));
            } else {
                setValidationError('');
            }
            setRefundAmount(value);
        },
        [refundableBalance, trls]
    );

    const handleCloseModal = useCallback(() => {
        setValidationError('');
        setRefundAmount('');
        onClose();
    }, [onClose]);

    const handleRefund = useCallback(() => {
        if (Number(refundAmount) === 0) {
            setValidationError(trls(TrlKeys.input.validation.empty));
            return;
        }
        if (validationError) {
            return;
        }

        void dispatch(
            fetchRefund({
                amount: refundAmount,
                setIsLoading,
                handleCloseModal,
            })
        );
    }, [refundAmount, validationError, dispatch, trls, handleCloseModal]);

    const buttons = (
        <>
            <Button mode="secondary" style="accent" onClick={handleCloseModal} stretched>
                {trls(TrlKeys.buttons.cancel)}
            </Button>
            <Button mode="primary" style="accent" onClick={handleRefund} loading={isLoading} stretched>
                {trls(TrlKeys.buttons.confirm)}
            </Button>
        </>
    );

    const actions = (
        <ActionBar
            type="vertical"
            primaryActions={isMobile ? buttons : <div className={styles.desktopButtonsWrapper}>{buttons}</div>}
        />
    );

    const description = (
        <Cell
            width="fit-content"
            gap={6}
            right={
                <QuestionCircleOutlinedSize16
                    ref={activatorRef}
                    initialColor="secondary"
                    onClick={isMobile ? setIsTooltipVisibleOn : undefined}
                    onMouseEnter={setIsTooltipVisibleOn}
                    onMouseLeave={setIsTooltipVisibleOff}
                />
            }
        >
            <CellText
                style={isMobile ? 'secondary' : 'primary'}
                typography={isMobile ? 'label-4-regular' : 'paragraph-2-regular'}
            >
                {trls(TrlKeys.description, [getFormattedIntNumber(refundableBalance)])}
            </CellText>
        </Cell>
    );

    const heldTip = heldRefundableBalance && (
        <>
            <Card borderRadius={24} padding={20} stretched style="secondary">
                <Text typography="paragraph-2-regular">{trls(TrlKeys.heldTip)}</Text>
            </Card>
            <VSpacing default={24} />
        </>
    );

    const content = (
        <NumberInput
            value={refundAmount}
            onChange={handleInputChange}
            invalid={!!validationError}
            errorMessage={validationError}
            placeholder={trls(TrlKeys.input.placeholder)}
            postfix={CURRENCY_SYMBOLS[AvailableCurrencies.RUR]}
            decimalLength={0}
            decimalMark={[]}
            allowNegative={false}
            size="large"
            data-qa="refund-modal-input"
        />
    );

    return (
        <>
            <Modal
                size="small"
                visible={visible}
                onClose={handleCloseModal}
                footer={actions}
                header={<MagritteModalHeader title={trls(TrlKeys.title)} subtitle={description} />}
            >
                {heldTip}
                {content}
            </Modal>
            <BottomSheet
                visible={visible}
                onClose={handleCloseModal}
                footer={actions}
                header={
                    <NavBar>
                        <NavBar.Pane>
                            <NavBar.Stage>
                                <NavBar.Actions centered>
                                    <NavBar.TitleContainer
                                        centered
                                        title={<Text typography="subtitle-1-semibold">{trls(TrlKeys.title)}</Text>}
                                        subtitle={description}
                                    />
                                </NavBar.Actions>
                            </NavBar.Stage>
                        </NavBar.Pane>
                    </NavBar>
                }
            >
                {heldTip}
                {content}
            </BottomSheet>
            <Layer layer="modal">
                <TooltipCommon
                    activatorRef={activatorRef}
                    tooltipContent={
                        <TextList>
                            {[
                                <Text key="balance">
                                    {trls(TrlKeys.tooltip.list1, [getFormattedIntNumber(realBalance)])}
                                </Text>,
                                <Text key="reserved">
                                    {trls(TrlKeys.tooltip.list2, [getFormattedIntNumber(displayReservedBalance)])}
                                </Text>,
                                displayHeldBalance > 0 && (
                                    <Text key="held">
                                        {trls(TrlKeys.tooltip.list3, [getFormattedIntNumber(displayHeldBalance)])}
                                    </Text>
                                ),
                                <Text key="refundable">
                                    {trls(TrlKeys.tooltip.list4, [getFormattedIntNumber(refundableBalance)])}
                                </Text>,
                                displayHeldBalance > 0 && heldRefundableBalance && (
                                    <Text key="held-note">{trls(TrlKeys.tooltip.list5)}</Text>
                                ),
                            ].filter(Boolean)}
                        </TextList>
                    }
                    visible={isTooltipVisible}
                    onClose={setIsTooltipVisibleOff}
                    bottomSheetTitle={trls(TrlKeys.title)}
                    placement="top-center"
                />
            </Layer>
        </>
    );
};
